import React, { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  InputAdornment,
  TextField,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { baseLightColors } from "@bn/theming";
import { BankWrapper } from "@bn/api/apiv1";
import { useBanksSearch } from "@api/v1/hooks/useBanksSearch";
import SearchIcon from "@icons/myicons/search.svg?react";
import { StyledListItem } from "@features/accounts/edit/components/CreateAndEditAccountForm";
import { SecurityImage } from "@components/SecurityImage";
import banksList from "@components/BankSelect/banksList";

const renderOption = (theme: Theme) => (props: Object, option: BankWrapper) => (
  <li {...props} key={option.bic}>
    <StyledListItem
      id={`bank-option-${option.id}`}
      data-testid={`bank-option-${option.bic}`}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box
          display="flex"
          alignItems="center"
          width="24px"
          height="24px"
          sx={{
            border: `1px solid ${theme.palette.border.primary}`,
            borderRadius: `4px`,
            backgroundColor: theme.palette.common.white,
            overflow: "hidden",
          }}
        >
          <img
            src={option.iconUrl}
            width="100%"
            alt="bank-logo"
            style={{
              objectFit: "contain",
            }}
          />
        </Box>
        <Typography variant="caption" color="textPrimary">
          {option.name}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="textPrimary">
          {option.bic}
        </Typography>
      </Box>
    </StyledListItem>
  </li>
);

const renderSearchInput =
  (bank?: BankWrapper | null, title?: string, placeholder?: string) =>
  (params: AutocompleteRenderInputParams) => (
    <Box position="relative">
      <TextField
        {...params}
        data-testid="bank-search"
        label={
          i18next.t(
            title ? title : "components.bankSearchField.label"
          ) as string
        }
        placeholder={i18next.t(
          placeholder ? placeholder : "components.bankSearchField.placeholder"
        )}
        sx={{
          "& .MuiInputBase-input": {
            paddingLeft: `2px !important`,
          },
        }}
        slotProps={{
          input: {
            ...params.InputProps,
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  marginLeft: (theme) => theme.spacing(3),
                }}
              >
                {bank?.iconUrl ? (
                  <SecurityImage
                    src={bank?.iconUrl || ""}
                    width={20}
                    height={20}
                  />
                ) : (
                  <Box color="icon.secondary" height="24px">
                    <SearchIcon width="24px" height="24px" />
                  </Box>
                )}
              </InputAdornment>
            ),
          },
          htmlInput: {
            ...params.inputProps,
            sx: {
              "&::placeholder": {
                color: "text.secondary",
                opacity: 1,
              },
            },
            "data-testid": "bank-search-input",
          },
        }}
      />
    </Box>
  );

const ListboxComponent = function ListboxComponent({
  ref,
  ...props
}: any & { ref?: React.Ref<HTMLUListElement> }) {
  const { t } = useTranslation();

  return (
    <>
      <Box display="flex" justifyContent="space-between" px={4} pt={2}>
        <Typography
          variant="overline"
          color={baseLightColors.N400}
          fontWeight={600}
        >
          {t("accountTypes.bankSelect.bankName")}
        </Typography>
        <Typography
          variant="overline"
          color={baseLightColors.N400}
          fontWeight={600}
        >
          {t("accountTypes.bankSelect.bic")}
        </Typography>
      </Box>
      <ul ref={ref} {...props} style={{ height: "33vh" }} />
    </>
  );
};

type Props = {
  title?: string;
  placeholder?: string;
  defaultBank?: BankWrapper | null;
  onBankClick?: (bank: BankWrapper | null) => void;
};

export const BankSearchField = ({
  title,
  placeholder,
  defaultBank,
  onBankClick,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = React.useState<BankWrapper[]>([]);
  const [debouncedSearchValue] = useDebounce(searchValue, 500);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedBank, setSelectedBank] = useState<BankWrapper | null>(
    defaultBank || null
  );

  const handleBankClick = (value: BankWrapper | null) => {
    setSelectedBank(value);
    onBankClick?.(value);
  };

  useEffect(() => {
    defaultBank && setSelectedBank(defaultBank);
  }, [defaultBank]);

  useBanksSearch(
    { search: debouncedSearchValue },
    {
      enabled: Boolean(debouncedSearchValue),
      onSuccess: (data) => {
        setOptions(data?.banks ?? []);
        setLoading(false);
      },
    }
  );
  useEffect(() => {
    if (!debouncedSearchValue) {
      setOptions(banksList as unknown as BankWrapper[]);
    }
  }, [debouncedSearchValue]);

  return (
    <Autocomplete
      id="bank-search"
      data-testid="bank-autocomplete"
      value={selectedBank}
      open={open}
      options={options}
      loading={loading}
      disablePortal
      renderOption={renderOption(theme)}
      renderInput={renderSearchInput(selectedBank, title, placeholder)}
      ListboxComponent={ListboxComponent}
      inputValue={searchValue}
      getOptionLabel={(option) => (option as BankWrapper).name}
      filterOptions={(options) => options}
      blurOnSelect
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      noOptionsText={t("accountTypes.bankSelect.noOptions")}
      onChange={(_, value) => handleBankClick((value as BankWrapper) || null)}
      onInputChange={(_, value: string) => {
        setLoading(!!value);
        setSearchValue(value);
      }}
    />
  );
};
